import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg, euro } from "@/lib/api";
import { AdminCard, PageTitle } from "@/pages/admin/AdminLayout";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const LANGS = [["en", "English"], ["it", "Italiano"], ["es", "Español"], ["fr", "Français"]];
const cls = "w-full h-11 px-4 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea";
const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 70);
const F = ({ label, hint, children, className = "" }) => (
  <div className={className}><label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">{label}</label>{children}{hint && <p className="text-[11px] text-ocean/45 mt-1">{hint}</p>}</div>
);
const blank = () => ({ slug: "", tour_slugs: ["", "", ""], image: "", active: true, order: 0, translations: Object.fromEntries(LANGS.map(([l]) => [l, { title: "", short: "" }])) });

export default function AdminPackages() {
  const [list, setList] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/packages").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); adminApi.get("/admin/tours").then((r) => setTours(r.data)); }, []);

  const toPayload = (p) => ({ slug: p.slug.trim(), tour_slugs: p.tour_slugs.filter((x) => x && x !== "none"), image: (p.image || "").trim(), active: p.active, order: Number(p.order) || 0, translations: p.translations });

  const open = (p) => {
    if (!p) return setEdit({ ...blank(), order: list.length, isNew: true });
    const ts = [...p.tour_slugs, "", "", ""].slice(0, 3);
    const trs = Object.fromEntries(LANGS.map(([l]) => [l, { title: p.translations?.[l]?.title || "", short: p.translations?.[l]?.short || "" }]));
    setEdit({ id: p.id, slug: p.slug, tour_slugs: ts, image: p.image, active: p.active, order: p.order, translations: trs });
  };

  const preview = edit ? (() => {
    const sel = edit.tour_slugs.filter((x) => x && x !== "none").map((s) => tours.find((x) => x.slug === s)).filter(Boolean);
    const full = sel.reduce((a, x) => a + x.price, 0);
    return { n: sel.length, full, price: Math.round(full * 0.85) };
  })() : null;

  const save = async () => {
    const body = toPayload(edit);
    if (!body.translations.en.title.trim()) return toast.error("Il titolo in inglese è obbligatorio");
    if (body.tour_slugs.length < 2) return toast.error("Scegli almeno 2 tour");
    setSaving(true);
    try {
      if (edit.isNew) await adminApi.post("/admin/packages", body);
      else await adminApi.put(`/admin/packages/${edit.id}`, body);
      toast.success("Pacchetto salvato");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const quick = async (p, patch) => {
    try {
      await adminApi.put(`/admin/packages/${p.id}`, toPayload({ ...p, ...patch }));
      setList((l) => l.map((x) => (x.id === p.id ? { ...x, ...patch } : x)));
      toast.success("Pacchetto aggiornato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare il pacchetto "${p.translations?.en?.title}"?`)) return;
    try { await adminApi.delete(`/admin/packages/${p.id}`); setList((l) => l.filter((x) => x.id !== p.id)); toast.success("Pacchetto eliminato"); } catch (e) { toast.error(errMsg(e)); }
  };

  const setTr = (l, k, v) => setEdit((p) => {
    const next = { ...p, translations: { ...p.translations, [l]: { ...p.translations[l], [k]: v } } };
    if (p.isNew && l === "en" && k === "title") next.slug = slugify(v);
    return next;
  });

  return (
    <div data-testid="admin-packages">
      <PageTitle title="Pacchetti" sub="Combina 2 o 3 tour in giorni consecutivi. Il prezzo è sempre la somma dei tour con il 15% di sconto."
        right={<button onClick={() => open(null)} className="inline-flex items-center gap-2 px-5 py-3 bg-ocean text-ivory text-sm font-medium rounded-xl btn-lift" data-testid="admin-package-new"><Plus size={16} />Nuovo pacchetto</button>} />
      <AdminCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ivory/70 text-[11px] uppercase tracking-[0.15em] text-ocean/55">
              <tr><th className="text-left px-5 py-3 font-medium">Pacchetto</th><th className="text-left px-5 py-3 font-medium">Tour inclusi</th><th className="text-right px-5 py-3 font-medium">Prezzo</th><th className="text-center px-5 py-3 font-medium">Attivo</th><th className="px-5 py-3" /></tr>
            </thead>
            <tbody className="divide-y divide-sand/60">
              {loading && <tr><td colSpan={5} className="px-5 py-10 text-center text-ocean/45">Caricamento...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-ocean/45">Nessun pacchetto.</td></tr>}
              {list.map((p) => (
                <tr key={p.id} className="hover:bg-sand/20" data-testid={`admin-package-row-${p.slug}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt="" className="w-14 h-10 rounded-lg object-cover bg-sand" />
                      <div><p className="text-ocean font-medium">{p.translations?.it?.title || p.translations?.en?.title}</p><p className="text-xs text-ocean/45">{p.days} giorni · max {p.max_guests}</p></div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-ocean/65">{p.tours.map((x) => x.titles?.it || x.titles?.en).join(" → ")}{!p.complete && <span className="block text-rose-700 mt-1">Un tour non esiste più: modifica il pacchetto</span>}</td>
                  <td className="px-5 py-3 text-right"><span className="text-xs text-ocean/45 line-through mr-2">{euro(p.full_price)}</span><span className="font-medium">{euro(p.price)}</span></td>
                  <td className="px-5 py-3 text-center"><Switch checked={p.active} onCheckedChange={(v) => quick(p, { active: v })} data-testid={`admin-package-active-${p.slug}`} /></td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-3 text-ocean/60">
                      <a href={`/packages/${p.slug}`} target="_blank" rel="noreferrer" aria-label="Apri" className="hover:text-ocean"><ExternalLink size={16} /></a>
                      <button onClick={() => open(p)} aria-label="Modifica" className="hover:text-ocean" data-testid={`admin-package-edit-${p.slug}`}><Pencil size={16} /></button>
                      <button onClick={() => remove(p)} aria-label="Elimina" className="text-rose-700" data-testid={`admin-package-delete-${p.slug}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      <Sheet open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <SheetContent className="w-full sm:max-w-2xl bg-white overflow-y-auto" data-testid="package-editor">
          <SheetHeader>
            <SheetTitle className="font-serif text-2xl text-ocean">{edit?.isNew ? "Nuovo pacchetto" : "Modifica pacchetto"}</SheetTitle>
            <SheetDescription>I tour si svolgono uno al giorno, nell'ordine scelto.</SheetDescription>
          </SheetHeader>
          {edit && (
            <div className="mt-6 space-y-5">
              {[0, 1, 2].map((i) => (
                <F key={i} label={`Giorno ${i + 1}${i === 2 ? " (facoltativo)" : ""}`}>
                  <Select value={edit.tour_slugs[i] || "none"} onValueChange={(v) => setEdit({ ...edit, tour_slugs: edit.tour_slugs.map((x, j) => (j === i ? v : x)) })}>
                    <SelectTrigger className="h-11 rounded-xl border-sand bg-white" data-testid={`package-editor-day-${i + 1}`}><SelectValue placeholder="Scegli un tour" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">
                      <SelectItem value="none">Nessuno</SelectItem>
                      {tours.map((x) => <SelectItem key={x.slug} value={x.slug}>{x.translations?.it?.title || x.translations?.en?.title} · {euro(x.price)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
              ))}
              {preview && preview.n >= 2 && (
                <p className="text-sm bg-sage-mist rounded-xl px-4 py-3 text-ocean" data-testid="package-editor-preview">{preview.n} giorni · <span className="line-through text-ocean/50">{euro(preview.full)}</span> → <strong>{euro(preview.price)}</strong> a persona (-15%)</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <F label="Slug (indirizzo web)" hint={`/packages/${edit.slug || "..."}`}><input className={cls} value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} data-testid="package-editor-slug" /></F>
                <F label="Ordine"><input type="number" className={cls} value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} data-testid="package-editor-order" /></F>
                <F label="Immagine (URL)" hint="Se vuota, usa la foto del primo tour" className="md:col-span-2"><input className={cls} value={edit.image} onChange={(e) => setEdit({ ...edit, image: e.target.value })} data-testid="package-editor-image" /></F>
              </div>
              <label className="flex items-center gap-3 text-sm text-ocean"><Switch checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: v })} data-testid="package-editor-active" />Visibile sul sito</label>
              <Tabs defaultValue="en">
                <TabsList className="bg-ivory">{LANGS.map(([l, n]) => <TabsTrigger key={l} value={l} data-testid={`package-editor-tab-${l}`}>{n}{l === "en" ? " *" : ""}</TabsTrigger>)}</TabsList>
                {LANGS.map(([l]) => (
                  <TabsContent key={l} value={l} className="space-y-4 mt-4">
                    <F label="Titolo"><input className={cls} value={edit.translations[l].title} onChange={(e) => setTr(l, "title", e.target.value)} data-testid={`package-editor-${l}-title`} /></F>
                    <F label="Descrizione breve"><textarea rows={3} className="w-full px-4 py-3 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea" value={edit.translations[l].short} onChange={(e) => setTr(l, "short", e.target.value)} data-testid={`package-editor-${l}-short`} /></F>
                  </TabsContent>
                ))}
              </Tabs>
              <div className="sticky bottom-0 bg-white pt-5 pb-2 border-t border-sand flex justify-end gap-3">
                <button onClick={() => setEdit(null)} className="px-5 py-3 rounded-xl border border-sand text-sm text-ocean" data-testid="package-editor-cancel">Annulla</button>
                <button onClick={save} disabled={saving} className="px-6 py-3 rounded-xl bg-ocean text-ivory text-sm font-medium disabled:opacity-60 btn-lift" data-testid="package-editor-save">{saving ? "Salvataggio..." : "Salva pacchetto"}</button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
